import React from 'react';
import { showcaseProjects } from '../../data';
import BackToTop from '../../sharedComponents/BackToTop';
import ExpandableImage from '../../sharedComponents/ExpandableImage';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import useScrollToTop from '../../hooks/useScrollToTop';

const ProjectDetail = ({ project, onBack }) => {
    const [scrollContainerRef, scrollToTop] = useScrollToTop();

    useDocumentTitle(project ? `${project.name} | Jerel Layog` : 'Project | Jerel Layog');

    if (!project) return null;

    const showcase = showcaseProjects?.find(s =>
        (project.slug && project.slug === s.slug) ||
        (project.link && project.link.includes(s.slug))
    );

    const getSlug = (text) => text ? text.toLowerCase().replace(/\s+/g, '-') : '';
    const tocItems = showcase?.sections?.filter(s => s.heading) || [];

    return (
        <>
            <section
                ref={scrollContainerRef}
                className="content-pane w-full flex flex-col pt-16 md:pt-24 2xl:pt-36 overflow-x-hidden px-4 sm:px-8 md:px-16 lg:px-24 2xl:px-32 relative z-20 pb-8 md:pb-32 2xl:pb-40 custom-scrollbar"
                tabIndex={-1}
                aria-labelledby="heading-proj-detail"
            >
                <div className="flex flex-col items-start gap-4 2xl:gap-6 mb-10 md:mb-16 2xl:mb-20 relative z-20 max-w-6xl 2xl:max-w-7xl">
                    <button
                        onClick={onBack}
                        aria-label="Navigate back to projects portfolio"
                        className="bg-black text-[var(--theme-accent)] font-bebas text-xl md:text-3xl 2xl:text-4xl px-4 2xl:px-6 py-2 2xl:py-3 border-2 md:border-4 2xl:border-[6px] border-white transform hover:-translate-x-2 2xl:hover:-translate-x-3 transition-transform shadow-[4px_4px_0_var(--theme-accent)] 2xl:shadow-[6px_6px_0_var(--theme-accent)] focus:outline-none focus-visible:ring-4 focus-visible:ring-white"
                    >
                        ◄ BACK TO PROJECTS
                    </button>
                    <h2
                        id="heading-proj-detail"
                        className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl 2xl:text-8xl font-bebas text-white bg-black px-4 2xl:px-6 py-2 2xl:py-3 border-4 md:border-[6px] 2xl:border-[8px] border-white shadow-[6px_6px_0_#000] md:shadow-[12px_12px_0_#000] 2xl:shadow-[18px_18px_0_#000] inline-block"
                    >
                        {project.name}
                    </h2>
                </div>

                <div className="flex flex-col lg:flex-row gap-10 md:gap-16 2xl:gap-20 w-full items-start">

                    <aside className="w-full lg:w-1/5 shrink-0 lg:sticky lg:top-8 2xl:lg:top-12 flex flex-col gap-8 2xl:gap-10 z-30">
                        <div className="relative">
                            <div className="absolute inset-0 bg-[var(--theme-accent)] transform translate-x-2 translate-y-2 2xl:translate-x-3 2xl:translate-y-3" aria-hidden="true"></div>
                            <div className="relative bg-black border-4 2xl:border-[6px] border-white p-5 md:p-7 2xl:p-9 flex flex-col gap-6 2xl:gap-8">

                                {tocItems.length > 0 && (
                                    <nav aria-label="Table of Contents" className="hidden lg:block">
                                        <h3 className="font-bebas text-[var(--theme-highlight)] text-2xl md:text-3xl 2xl:text-4xl tracking-wide border-b-4 2xl:border-b-[6px] border-white pb-2 2xl:pb-3 mb-3 2xl:mb-4">
                                            CONTENTS
                                        </h3>
                                        <ul className="flex flex-col gap-3 2xl:gap-4 font-montserrat font-bold text-white text-sm md:text-base 2xl:text-lg uppercase">
                                            {tocItems.map((section, i) => (
                                                <li key={i}>
                                                    <a
                                                        href={`#${getSlug(section.heading)}`}
                                                        className="hover:text-[var(--theme-accent)] hover:translate-x-2 inline-block transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                                                    >
                                                        {section.heading}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </nav>
                                )}

                                {tocItems.length > 0 && <div className="hidden lg:block w-full h-1 bg-gray-800" aria-hidden="true"></div>}

                                <div className="flex flex-col gap-4">
                                    {project.role && (
                                        <div>
                                            <p className="font-bebas text-[var(--theme-highlight)] text-xl md:text-2xl 2xl:text-3xl tracking-wide">ROLE</p>
                                            <p className="font-montserrat font-bold text-white text-base md:text-lg 2xl:text-xl">{project.role}</p>
                                        </div>
                                    )}
                                    {project.timeline && (
                                        <div>
                                            <p className="font-bebas text-[var(--theme-highlight)] text-xl md:text-2xl 2xl:text-3xl tracking-wide">TIMELINE</p>
                                            <p className="font-montserrat font-bold text-white text-base md:text-lg 2xl:text-xl">{project.timeline}</p>
                                        </div>
                                    )}
                                    {project.tags?.length > 0 && (
                                        <div>
                                            <p className="font-bebas text-[var(--theme-highlight)] text-xl md:text-2xl 2xl:text-3xl tracking-wide mb-2 2xl:mb-3">TECH STACK</p>
                                            <ul aria-label="Technologies used" className="flex flex-wrap gap-2 2xl:gap-3">
                                                {project.tags.map(tag => (
                                                    <li key={tag} className="font-bold text-sm 2xl:text-base bg-white text-black px-2 py-1 2xl:px-3 2xl:py-1.5 border-2 2xl:border-[3px] border-black">
                                                        {tag}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {(project.externalLink || (project.link && project.link.startsWith('http'))) && (
                                        <a
                                            href={project.externalLink || project.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-bebas text-xl md:text-2xl 2xl:text-3xl bg-[var(--theme-accent)] text-black px-6 py-2 2xl:px-8 2xl:py-3 border-4 2xl:border-[6px] border-black hover:bg-black hover:text-[var(--theme-accent)] hover:border-[var(--theme-accent)] transition-colors text-center shadow-[4px_4px_0_#000] 2xl:shadow-[6px_6px_0_#000] focus:outline-none focus-visible:ring-4 focus-visible:ring-white mt-2 2xl:mt-4"
                                        >
                                            VIEW PROJECT ↗
                                            <span className="sr-only"> (opens in a new tab)</span>
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    </aside>

                    <div className="flex-1 flex flex-col gap-10 2xl:gap-12 min-w-0">

                        {project.image && (
                            <div className="relative">
                                <div className="absolute inset-0 bg-black transform translate-x-3 translate-y-3 2xl:translate-x-4 2xl:translate-y-4" aria-hidden="true"></div>
                                <img
                                    src={project.image}
                                    alt={`Showcase cover for ${project.name}`}
                                    className="relative border-4 md:border-[6px] 2xl:border-[8px] border-black w-full object-cover shadow-[6px_6px_0_rgba(0,0,0,0.5)] 2xl:shadow-[10px_10px_0_rgba(0,0,0,0.5)]"
                                />
                            </div>
                        )}

                        {project.desc && (
                            <div className="relative">
                                <div className="absolute inset-0 bg-black transform translate-x-1.5 translate-y-1.5 md:translate-x-2.5 md:translate-y-2.5 2xl:translate-x-4 2xl:translate-y-4" aria-hidden="true"></div>
                                <div className="relative bg-white border-4 md:border-[6px] 2xl:border-[8px] border-black p-6 md:p-8 2xl:p-10">
                                    <p className="font-montserrat font-semibold md:font-bold text-black text-base md:text-lg 2xl:text-xl leading-relaxed 2xl:leading-loose">{project.desc}</p>
                                </div>
                            </div>
                        )}

                        {showcase?.sections?.map((section, i) => {
                            const sectionId = getSlug(section.heading);

                            if (section.type === 'text') return (
                                <div key={i} id={sectionId} className="flex flex-col gap-3 2xl:gap-4 scroll-mt-10 2xl:scroll-mt-16">
                                    {(section.heading || section.subheading) && (
                                        <div className="flex flex-col items-start gap-2 2xl:gap-3 mb-1 2xl:mb-2">
                                            {section.heading && (
                                                <h3 className="font-bebas text-2xl md:text-4xl 2xl:text-5xl text-white bg-black px-4 py-1 md:px-5 md:py-2 2xl:px-6 2xl:py-3 border-2 md:border-4 2xl:border-[6px] border-white shadow-[4px_4px_0_#000] 2xl:shadow-[6px_6px_0_#000]">
                                                    {section.heading}
                                                </h3>
                                            )}
                                            {section.subheading && (
                                                <h4 className="font-bebas text-lg md:text-2xl 2xl:text-3xl text-[var(--theme-accent)] bg-black px-3 py-1 2xl:px-4 2xl:py-2 border-2 2xl:border-[3px] border-white shadow-[2px_2px_0_#000] 2xl:shadow-[4px_4px_0_#000]">
                                                    {section.subheading}
                                                </h4>
                                            )}
                                        </div>
                                    )}

                                    {section.content && (
                                        <div className="relative">
                                            <div className="absolute inset-0 bg-black transform translate-x-1.5 translate-y-1.5 md:translate-x-2.5 md:translate-y-2.5 2xl:translate-x-4 2xl:translate-y-4" aria-hidden="true"></div>
                                            <div className="relative bg-white border-4 md:border-[6px] 2xl:border-[8px] border-black p-5 md:p-6 2xl:p-8">
                                                <p className="font-montserrat text-black text-base 2xl:text-lg leading-relaxed 2xl:leading-loose">{section.content}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );

                            if (section.type === 'image') return (
                                <figure key={i} className="flex flex-col gap-2 mt-4">
                                    <ExpandableImage src={section.src} alt={section.alt || "Project showcase visual"} variant="persona" />
                                    {section.caption && (
                                        <figcaption className="font-montserrat text-sm text-white italic mt-1 bg-black p-2 w-fit">{section.caption}</figcaption>
                                    )}
                                </figure>
                            );

                            if (section.type === 'links') return (
                                <div key={i} className="flex flex-wrap gap-4 2xl:gap-5 mt-6 2xl:mt-8" aria-label="Additional resources">
                                    {section.links?.map((l, li) => (
                                        <a
                                            key={li}
                                            href={l.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-bebas text-xl 2xl:text-2xl bg-[var(--theme-accent)] text-black px-6 py-2 2xl:px-8 2xl:py-3 border-4 2xl:border-[6px] border-black hover:bg-black hover:text-[var(--theme-accent)] transition-colors shadow-[4px_4px_0_#000] 2xl:shadow-[6px_6px_0_#000] focus:outline-none focus-visible:ring-4 focus-visible:ring-white"
                                        >
                                            {l.text} ↗
                                            <span className="sr-only"> (opens in a new tab)</span>
                                        </a>
                                    ))}
                                </div>
                            );
                            return null;
                        })}
                    </div>
                </div>

                <div className="md:hidden flex justify-center w-full mt-16 mb-4 relative z-50">
                    <BackToTop variant="persona" onClick={scrollToTop} className="relative hover:-translate-y-1" />
                </div>
            </section>

            <div className="hidden md:block">
                <BackToTop variant="persona" onClick={scrollToTop} />
            </div>
        </>
    );
};

export default ProjectDetail;